import get from 'lodash/get'
import axios from 'axios'
import isArray from 'lodash/isArray'
import createError from 'axios/lib/core/createError'

export const ERROR_TYPE_LEGAL = 'ELEGAL'

/**
 * @class ApiClient
 * This class represents API client used for back-end server communication.
 * It uses "axios" http client for sending ajax requests.
 * @see https://github.com/axios/axios
 */
export default class ApiClient {
  /**
   * Create new ApiClient instance.
   *
   * @param  {Object} options
   */
  constructor (options) {
    this.settings = Object.assign({
      baseUrl: '', // No trailing slash
      resourceSeparator: '/'
    }, options)
  }

  /**
   * Set multiple API client options.
   *
   * @param {Object} settings
   * @return {ApiClient}
   */
  setOptions (options) {
    this.settings = Object.assign(this.settings, options)
    return this
  }

  /**
   * Get API server base url.
   * Get access token.
   *
   * @return {String}
   */
  getBaseUrl () {
    return this.settings.baseUrl
  }

  /**
   * Create "request" config object.
   * @see  https://github.com/axios/axios
   *
   * @param  {String} method
   * @param  {String|Array} resource
   * @param  {Object} data
   * @return {Object}
   */
  createRequest (method, resource, data) {
    const request = {
      method,
      baseURL: this.getBaseUrl(),
      url: this.createResourcePath(resource)
    }

    request[(method === 'GET') ? 'params' : 'data'] = data

    return request
  }

  /**
   * Send request to BE server.
   *
   * @param  {Object} request
   * @return {Promise}
   */
  _sendRequest (request) {
    return axios(request)
      .then(response => {
        // Forward business logic error response to fail handler
        if (response.data.status !== undefined && response.data.status !== 'SUCCESS') {
          throw createError(
            this.extractErrorMessage(response),
            response.config,
            ERROR_TYPE_LEGAL,
            response.request,
            response
          )
        }
        return response
      })
  }

  /**
   * Send unsigned API request.
   * @see this._sendRequest()
   *
   * @param  {String} method
   * @param  {Array|String} resource
   * @param  {Object} [data]
   * @return {Promise}
   */
  sendRequest (method, resource, data) {
    return this._sendRequest(this.createRequest(method, resource, data))
      .then(response => {
        return response.data
      })
  }

  /**
   * Make resource path string from array or string.
   *
   * Examples:
   *   this.createResourcePath(['user', 1]) === 'user/1';
   *   this.createResourcePath('user') === 'user';
   *
   * @param  {String|Array} resource
   * @return {String}
   */
  createResourcePath (resource) {
    if (isArray(resource)) {
      return resource.join(this.settings.resourceSeparator)
    }
    return resource
  }

  /**
   * Get first error message from response.
   *
   * @param  {Object} response Axios response object
   * @return {String}
   */
  extractErrorMessage (response) {
    return get(response, 'data.errors[0].message', 'Unknown error.')
  }
}



// WEBPACK FOOTER //
// ./src/services/ApiClient/ApiClient.js